import * as React from 'react';
import { Card, notify, Page, Input } from 'sha-el-design';
import { EmployeeDropdown } from '../Common/EmployeeDropdown';
import { EmployeeNodeEdge, StateNodeEdge, EmailSettingMutationInput } from '../../../schema';
import { StateDropdown } from '../Common/StateDropdown';
import { EmailEmployeeList } from './EmailEmployeeList';
import { Inject } from '../../helpers';
import { EmailService } from '../../service/emailService';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import { MdHome, MdArrowBack } from 'react-icons/md';
import { Link } from 'react-router-dom';
import '../Common/listPageStyles';

// send_email() in views.py always includes this address, on top of whatever is
// configured here. Showing it means the screen matches who actually receives
// the report instead of quietly under-reporting by one.
const ALWAYS_SENT_TO = 'mohit.jalan.4u@gmail.com';

export class Email extends React.Component<{}, State> {

  @Inject emailService: EmailService;

  constructor(prop: {}) {
    super(prop);
    this.state = {
      employee: null,
      state: null,
      stateEmployee: null,
      busyGlobal: false,
      busyState: false,
      refreshKey: 0,
      globalIds: [],
      stateIds: [],
    };
  }

  refresh = () => this.setState({ refreshKey: this.state.refreshKey + 1 })

  submit = (isGlobal: boolean) => {
    const { employee, state, stateEmployee } = this.state;
    if (isGlobal && !employee) {
      return notify({ type: 'error', title: 'Pick an employee first', duration: 3000 });
    }
    if (!isGlobal && (!state || !stateEmployee)) {
      return notify({
        type: 'error',
        title: 'Pick a state and an employee',
        duration: 3000,
      });
    }

    const chosen = isGlobal ? employee : stateEmployee;
    const chosenId = nestedAccess(chosen, 'node', 'id');
    const chosenName = nestedAccess(chosen, 'node', 'name');

    // Refuse a second row for someone already on the list. Nothing dedupes:
    // EmailSetting has no unique constraint, and send_email concatenates the
    // global and per-state rows without de-duplicating, so a person who is
    // both gets the report twice.
    //
    // The cross-panel case is the one worth blocking, because it is invisible:
    // the two panels are separate queries, so while working the state panel
    // you cannot see that someone is already a global recipient.
    if (this.state.globalIds.indexOf(chosenId) !== -1) {
      return notify({
        type: 'error',
        title: `${chosenName || 'That employee'} already gets every report`,
        message: isGlobal
          ? 'They are already on the every-report list.'
          : 'Global recipients receive this state too — adding them here would send the report twice.',
        duration: 5000,
      });
    }
    if (!isGlobal && this.state.stateIds.indexOf(chosenId) !== -1) {
      return notify({
        type: 'error',
        title: `${chosenName || 'That employee'} already gets this state's reports`,
        duration: 4000,
      });
    }

    const address = nestedAccess(chosen, 'node', 'emailid');
    if (!address || !String(address).trim()) {
      return notify({
        type: 'error',
        title: 'That employee has no email address',
        message: 'Add one to their employee record first, or the report will fail to send.',
        duration: 5000,
      });
    }

    isGlobal ? this.setState({ busyGlobal: true }) : this.setState({ busyState: true });
    const data: EmailSettingMutationInput = {
      employee: nestedAccess(chosen, 'node', 'id'),
      state: isGlobal ? null : nestedAccess(state, 'node', 'id'),
      isGlobal,
    };

    const done = () => (isGlobal
      ? this.setState({ busyGlobal: false })
      : this.setState({ busyState: false }));

    this.emailService.add(
      data,
      (response) => {
        // The mutation now selects `errors`; without it a rejected write came
        // back as an ordinary 200 with id: null and this reported success.
        const errors = nestedAccess(response, 'data', 'createEmailSetting', 'errors');
        done();
        if (errors && errors.length) {
          return notify({
            type: 'error',
            title: 'Could not add recipient',
            message: errors[0].messages[0],
            duration: 4500,
          });
        }
        notify({ type: 'success', title: 'Recipient added', duration: 2000 });
        // Clear the picker and refetch in place. This used to be
        // `setTimeout(() => window.location.href = '/dashboard/email/', 1000)`
        // -- a full page reload a second after the toast.
        isGlobal ? this.setState({ employee: null }) : this.setState({ stateEmployee: null });
        this.refresh();
      },
      () => {
        done();
        notify({ type: 'error', title: 'Could not reach the server', duration: 3500 });
      },
    );
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Email Settings', link: '/dashboard/email' },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  render() {
    const { employee, state, stateEmployee, busyGlobal, busyState, refreshKey } = this.state;
    return (
      <div className='cs-list'>
        <Page
          title={'Email Settings'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard'}>
              <MdArrowBack />
            </Link>
          }
        >
          <div className='cs-mail__grid'>
            <div className='cs-mail__panel'>
              <Card title='Every report'>
                <p className='cs-mail__hint'>
                  These people receive the completion report for every allocation, in every state.
                </p>
                <div className='cs-mail__add'>
                  <div className='cs-rep__field'>
                    <EmployeeDropdown
                      value={employee}
                      inputProps={{}}
                      uniqueIdentifier={(v) => v.node.id}
                      displayProp={(v) => v && v.node.name}
                      onChange={(v) => this.setState({ employee: v })}
                      label='Add an employee'
                      mode='single'
                    />
                  </div>
                  <button
                    type='button'
                    className='cs-rep__search'
                    onClick={() => this.submit(true)}
                    disabled={busyGlobal}
                  >
                    {busyGlobal ? 'Adding...' : 'Add'}
                  </button>
                </div>

                <EmailEmployeeList
                  isGlobal={true}
                  refreshKey={refreshKey}
                  onChanged={this.refresh}
                  onLoaded={(ids) => this.setState({ globalIds: ids })}
                  existingEmployeeIds={this.state.globalIds}
                />

                <div className='cs-mail__fixed'>
                  <span>
                    Always included: <b>{ALWAYS_SENT_TO}</b> — fixed in the report code, not
                    editable here.
                  </span>
                </div>
              </Card>
            </div>

            <div className='cs-mail__panel'>
              <Card title='One state only'>
                <p className='cs-mail__hint'>
                  These people receive reports only for allocations in the state you choose.
                </p>
                <div className='cs-mail__add cs-mail__add--stacked'>
                  <div className='cs-rep__field'>
                    <StateDropdown
                      mode='single'
                      value={state}
                      onChange={(v) => this.setState({ state: v, stateEmployee: null })}
                      uniqueIdentifier={(v) => v && v.node.id}
                      displayProp={(v) => (v && v.node.name) || ''}
                      label='State'
                    />
                  </div>
                </div>
                <div className='cs-mail__add'>
                  <div className='cs-rep__field'>
                    {state ? (
                      <EmployeeDropdown
                        value={stateEmployee}
                        inputProps={{}}
                        uniqueIdentifier={(v) => v.node.id}
                        displayProp={(v) => v && v.node.name}
                        onChange={(v) => this.setState({ stateEmployee: v })}
                        label='Add an employee'
                        mode='single'
                      />
                    ) : (
                      // Held in the layout rather than appearing from nowhere,
                      // so the panel does not jump as you fill it in.
                      <Input label='Add an employee' value='' disabled placeholder='Pick a state first' />
                    )}
                  </div>
                  <button
                    type='button'
                    className='cs-rep__search'
                    onClick={() => this.submit(false)}
                    disabled={busyState || !state}
                  >
                    {busyState ? 'Adding...' : 'Add'}
                  </button>
                </div>

                <EmailEmployeeList
                  isGlobal={false}
                  state={state}
                  refreshKey={refreshKey}
                  onChanged={this.refresh}
                  onLoaded={(ids) => this.setState({ stateIds: ids })}
                  existingEmployeeIds={this.state.stateIds}
                />
              </Card>
            </div>
          </div>
        </Page>
      </div>
    );
  }
}

interface State {
  employee: EmployeeNodeEdge;
  state: StateNodeEdge;
  stateEmployee: EmployeeNodeEdge;
  busyGlobal: boolean;
  busyState: boolean;
  refreshKey: number;
  /** employee ids already on each list, used to block duplicate recipients */
  globalIds: string[];
  stateIds: string[];
}
